import React, { useState, useEffect, useRef } from 'react';
import * as faceapi from 'face-api.js';
import { CameraIcon } from '@heroicons/react/24/outline'; // Import an icon

interface CameraCaptureProps {
  onCapture: (file: File) => void;
}

const COUNT_DOWN_STEPS = 2;
export default function CameraCapture({ onCapture }: CameraCaptureProps) {
  const messagePositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDetecting, setIsDetecting] = useState<boolean>(false);
  const [faceMessage, setFaceMessage] = useState<string>();
  const [isAligned, setIsAligned] = useState<boolean>(false);
  const [countdownActive, setCountdownActive] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(COUNT_DOWN_STEPS);
  const [deviceType, setDeviceType] = useState<'mobile' | 'desktop'>('desktop');
  const [scaleFactor, setScaleFactor] = useState<number>(1);
  const [showCaptureButton, setShowCaptureButton] = useState<boolean>(true);
  const [showGuidanceBox, setShowGuidanceBox] = useState<boolean>(false);

  const alignmentTimerRef = useRef<NodeJS.Timeout | null>(null);

  const TARGET_MODEL_SIZE = 224; // Model's target size
  const TARGET_HEIGHT = 720;    // Height we'll resize to before processing

  const getFaceDimensions = () => {
    if (!containerRef.current || !videoRef.current) return {
      ideal: TARGET_MODEL_SIZE,
      min: TARGET_MODEL_SIZE * 0.7,
      max: TARGET_MODEL_SIZE * 1.3,
      tolerance: TARGET_MODEL_SIZE * 0.2
    };

    // Get actual video dimensions
    const videoWidth = videoRef.current.videoWidth;
    const videoHeight = videoRef.current.videoHeight;

    // Calculate what our scale will be when we resize to 720p
    const resizeScale = TARGET_HEIGHT / videoHeight;

    // Calculate how big our detection box should be in the current resolution
    // to give us roughly 224px after resize to 720p
    const idealSizeInCurrentRes = Math.round(TARGET_MODEL_SIZE / resizeScale);

    return {
      ideal: idealSizeInCurrentRes,
      min: Math.round(idealSizeInCurrentRes * 0.75),  // Allow 25% smaller
      max: Math.round(idealSizeInCurrentRes * 1.25),  // Allow 25% larger
      tolerance: Math.round(idealSizeInCurrentRes * 0.5) // 50% of ideal size for centering tolerance
    };
  };

  // Device detection
  useEffect(() => {
    const checkDevice = () => {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setDeviceType(isMobile ? 'mobile' : 'desktop');
    };
    checkDevice();
  }, []);

  // Consolidated cleanup effect
  useEffect(() => {
    let detectionInterval: NodeJS.Timeout;
    let resizeObserver: ResizeObserver;

    // Setup function
    const setup = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setFaceMessage('Your browser doesn\'t support camera access');
          return;
        }

        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri('./models'),
        ]);
        await startCamera();
      } catch (err) {
        console.error('Setup error:', err);
        setFaceMessage('Error during setup.');
      }
    };

    // Initialize resize observer
    const updateScaleFactor = () => {
      if (containerRef.current && videoRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const newScale = containerWidth / 1280;
        setScaleFactor(newScale);
      }
    };

    resizeObserver = new ResizeObserver(updateScaleFactor);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Add orientation change listener
    window.addEventListener('orientationchange', handleOrientationChange);

    // Run setup
    setup();

    // Cleanup function
    return () => {
      if (detectionInterval) clearInterval(detectionInterval);
      if (alignmentTimerRef.current) clearTimeout(alignmentTimerRef.current);

      // Stop the camera
      stopCamera();

      // Clean up observers
      if (resizeObserver) resizeObserver.disconnect();

      // Remove event listeners
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []); // Empty dependency array for component mount/unmount

  const handleOrientationChange = () => {
    setTimeout(() => {
      if (videoRef.current && canvasRef.current) {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        drawGuidanceBox();
      }
    }, 100);
  };

  const stopCamera = () => {
    // Clean up stream from ref
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
        console.log("Track stopped:", track.label);
      });
      streamRef.current = null;
    }
    // Clean up video element
    if (videoRef.current) {
      const stream = videoRef.current.srcObject as MediaStream;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async () => {
    try {
      const idealWidth = deviceType === 'mobile' ? 720 : 1280;
      const idealHeight = deviceType === 'mobile' ? 1280 : 720;

      const constraints = {
        video: {
          facingMode: 'user',
          width: { ideal: idealWidth },
          height: { ideal: idealHeight }
        }
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = mediaStream;

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.playsInline = true;
        await videoRef.current.play();

        videoRef.current.onloadedmetadata = () => {
          if (showGuidanceBox) {
            drawGuidanceBox();
          }
        };
      }
    } catch (error) {
      console.error('Camera access error:', error);
      if (error instanceof DOMException) {
        switch (error.name) {
          case 'NotAllowedError':
            setFaceMessage('Camera access denied. Please check your permissions.');
            break;
          case 'NotFoundError':
            setFaceMessage('No camera found. Please ensure your device has a camera.');
            break;
          case 'NotReadableError':
            setFaceMessage('Camera is in use by another application.');
            break;
          default:
            setFaceMessage('Camera error. Please try again.');
        }
      }
      setIsDetecting(false);
    }
  };

  const drawGuidanceBox = () => {
    if (!canvasRef.current || !videoRef.current) return;
    if (!showGuidanceBox) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;

    // Update canvas dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get dynamic dimensions
    const dimensions = getFaceDimensions();
    const boxSize = dimensions.ideal;

    // Calculate box position
    const boxX = (canvas.width - boxSize) / 2;
    const boxY = (canvas.height - boxSize) / 2;

    // Store the position for the message
    messagePositionRef.current = {
      x: boxX,
      y: boxY + boxSize // Position right under the box
    };

    // Clear and draw
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = isAligned ? '#4ade80' : '#94a3b8';
    ctx.lineWidth = Math.max(3, 4 * scaleFactor);
    ctx.strokeRect(boxX, boxY, boxSize, boxSize);

    if (isAligned) {
      ctx.fillStyle = 'rgba(74, 222, 128, 0.1)';
      ctx.fillRect(0, 0, canvas.width, boxY);
      ctx.fillRect(0, boxY, boxX, boxSize);
      ctx.fillRect(boxX + boxSize, boxY, canvas.width - (boxX + boxSize), boxSize);
      ctx.fillRect(0, boxY + boxSize, canvas.width, canvas.height - (boxY + boxSize));
    }
  };

  const isFaceCentered = (face: faceapi.Box, videoWidth: number, videoHeight: number) => {
    const dimensions = getFaceDimensions();
    const faceCenter = {
      x: face.x + face.width / 2,
      y: face.y + face.height / 2
    };

    const videoCenter = {
      x: videoWidth / 2,
      y: videoHeight / 2
    };

    const xOffset = Math.abs(faceCenter.x - videoCenter.x);
    const yOffset = Math.abs(faceCenter.y - videoCenter.y);

    return xOffset <= dimensions.tolerance && yOffset <= dimensions.tolerance;
  };

  useEffect(() => {
    let detectionInterval: NodeJS.Timeout;

    if (videoRef.current && isDetecting) {
      detectionInterval = setInterval(async () => {
        if (!videoRef.current) return;

        const detections = await faceapi.detectAllFaces(
          videoRef.current,
          new faceapi.TinyFaceDetectorOptions()
        );

        if (detections.length > 0) {
          const face = detections[0];
          const { width: faceWidth } = face.box;
          const dimensions = getFaceDimensions();

          const isCorrectDistance = faceWidth > dimensions.min && faceWidth < dimensions.max;
          const isCentered = isFaceCentered(face.box, videoRef.current.videoWidth, videoRef.current.videoHeight);

          if (isCorrectDistance && isCentered) {
            if (!isAligned) {
              setFaceMessage('Perfect! Hold still...');
              setIsAligned(true);
              alignmentTimerRef.current = setTimeout(() => {
                setCountdownActive(true);
              }, 1500);
            }
          } else {
            if (countdownActive) {
              setCountdownActive(false);
              setCountdown(COUNT_DOWN_STEPS);
            }

            if (alignmentTimerRef.current) {
              clearTimeout(alignmentTimerRef.current);
            }

            setIsAligned(false);

            if (!isCorrectDistance) {
              setFaceMessage(
                faceWidth <= dimensions.min
                  ? 'Too far away. Please move closer.'
                  : 'Too close. Please move back.'
              );
            } else if (!isCentered) {
              setFaceMessage('Please center your head in the box.');
            }
          }
        } else {
          setFaceMessage('Please align your face and head in the box.');
          setIsAligned(false);
          if (alignmentTimerRef.current) {
            clearTimeout(alignmentTimerRef.current);
          }
          if (countdownActive) {
            setCountdownActive(false);
            setCountdown(COUNT_DOWN_STEPS);
          }
        }

        if(showGuidanceBox) {
          drawGuidanceBox();
        }
      }, 100);
    }

    return () => {
      if (detectionInterval) clearInterval(detectionInterval);
    };
  }, [isDetecting, isAligned, countdownActive, scaleFactor, showGuidanceBox]);

  useEffect(() => {
    let countdownInterval: NodeJS.Timeout;

    if (countdownActive && countdown > 0) {
      setFaceMessage(`Capturing in ${countdown}...`);
      countdownInterval = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      handleCapture();
    }

    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [countdownActive, countdown]);

  const handleCaptureClick = () => {
    setShowCaptureButton(false); // Hide the button
    setIsDetecting(true);     // Start face detection
    setShowGuidanceBox(true); // Show the guidance box
  }

  const handleCapture = async () => {
    if (!videoRef.current) return;
    setIsDetecting(false);

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(videoRef.current, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'camera_capture.png', { type: 'image/png' });
        onCapture(file);
      }
    }, 'image/png');
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative overflow-hidden rounded-lg">
        <video
          ref={videoRef}
          className={`w-full object-cover ${isAligned ? 'ring-4 ring-green-400 transition-all duration-200' : ''}`}
          muted
          playsInline
          autoPlay
          style={{ transform: 'scaleX(-1)' }}
          onLoadedMetadata={() => {
            drawGuidanceBox();
          }}
        />
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{ transform: 'scaleX(-1)' }}
        />

        {/* Conditional Rendering for Button */}
        {showCaptureButton && (
          <div className='absolute inset-0 flex items-center justify-center'>
            <button
              onClick={handleCaptureClick}
              className='inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
            >
              <CameraIcon className='h-5 w-5 mr-2' />
              Begin Capture
            </button>
          </div>
        )}
        <div
          className='absolute pointer-events-none'
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            top: `${(messagePositionRef.current.y + 15) * (containerRef.current?.clientWidth || 1) / (videoRef.current?.videoWidth || 1)}px`,
            padding: '4px 12px',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            borderRadius: '4px',
            fontSize: `${Math.max(14, 16 * scaleFactor)}px`,
            textAlign: 'center',
            whiteSpace: 'nowrap',
            zIndex: 10
          }}
        >
          {faceMessage}
        </div>
      </div>
    </div>
  );
}